// Form Schema Types - Code-first approach

export type FieldType = 
  | 'text' 
  | 'textarea' 
  | 'number' 
  | 'date' 
  | 'select' 
  | 'checkbox' 
  | 'radio' 
  | 'phone' 
  | 'email' 
  | 'address' 
  | 'file' 
  | 'signature'
  | 'country'
  | 'ssn'
  | 'currency';

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormFieldValidation {
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  message?: string;
}

export interface FormFieldConditional {
  field: string;
  operator?: 'equals' | 'notEquals' | 'contains' | 'exists';
  value: string | boolean | string[];
}

export interface FormField {
  key: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  validation?: FormFieldValidation;
  options?: FormFieldOption[];
  conditional?: FormFieldConditional;
  defaultValue?: string | boolean | number;
  fullWidth?: boolean;
  // For address type
  addressFields?: ('street' | 'city' | 'state' | 'zip' | 'country')[];
}

export interface FormSection {
  key: string;
  title: string;
  description?: string;
  fields: FormField[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export interface FormTemplateSettings {
  allowSaveDraft: boolean;
  showProgress: boolean;
  requireSignature: boolean;
  confirmationMessage: string;
  expiresInDays?: number;
  notifyOnComplete?: boolean;
}

export interface FormTemplate {
  id: string;
  name: string;
  description: string;
  category: 'intake' | 'family' | 'employment' | 'humanitarian' | 'citizenship' | 'evidence' | 'general';
  visaType?: string;
  sections: FormSection[];
  settings: FormTemplateSettings;
  version?: number;
}

// Response types
export interface FormResponses {
  [fieldKey: string]: string | boolean | number | null | undefined | AddressValue;
}

export interface AddressValue {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

// Database types
export interface ClientFormTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  visa_type: string | null;
  schema: FormTemplate;
  is_active: boolean;
  is_base_template: boolean;
  parent_template_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientFormResponse {
  id: string;
  template_id: string;
  contact_id: string | null;
  deal_id: string | null;
  case_id: string | null;
  response_token: string;
  status: 'pending' | 'in_progress' | 'completed' | 'expired';
  responses: FormResponses;
  completed_at: string | null;
  expires_at: string | null;
  sent_via: 'whatsapp' | 'email' | 'link' | null;
  sent_by: string | null;
  created_at: string;
  updated_at: string;
}

// Helper to generate unique token
export function generateResponseToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 32; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Helper to count total fields
export function countTotalFields(template: FormTemplate): number {
  return template.sections.reduce((acc, section) => acc + section.fields.length, 0);
}

// Helper to count filled fields
export function countFilledFields(template: FormTemplate, responses: FormResponses): number {
  let count = 0;
  template.sections.forEach(section => {
    section.fields.forEach(field => {
      const value = responses[field.key];
      if (value !== undefined && value !== null && value !== '') {
        count++;
      }
    });
  });
  return count;
}

// Helper to calculate progress percentage
export function calculateProgress(template: FormTemplate, responses: FormResponses): number {
  const total = countTotalFields(template);
  if (total === 0) return 100;
  const filled = countFilledFields(template, responses);
  return Math.round((filled / total) * 100);
}
